import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Pagination,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Button,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  ListItemText,
  Slider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  BarChart as BarChartIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useFolder } from '../contexts/FolderContext';
import { isNewModelFormatFolder } from '../utils/modelFormat';
import { filterAllowedItems } from '../utils/allowedItemsFilter';
import {
  COMBO_AGGREGATE_ITEMNAME,
  normalizeItemKey,
  isComboAggregateItem,
  compareItemDisplayOrder,
  displayItemLabel,
  buildComboAggregateStrictRows,
  summarizeComboAggregateMetrics,
} from '../utils/comboAggregate';
import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

const getForecastBandFields = (row) => ({
  lower: row?.forecast_qty_lower_component_model ?? row?.forecast_qty_lower,
  upper: row?.forecast_qty_upper_component_model ?? row?.forecast_qty_upper,
});

const getSessionBandFields = (sessionRow) => {
  const lower =
    sessionRow?.session_forecast_lower
    ?? sessionRow?.forecast_qty_lower_component_model
    ?? sessionRow?.forecast_qty_lower;
  const upper =
    sessionRow?.session_forecast_upper
    ?? sessionRow?.forecast_qty_upper_component_model
    ?? sessionRow?.forecast_qty_upper;
  return { lower, upper };
};

const formatForecastRange = (lower, upper) => {
  const lo = parseFloat(lower);
  const hi = parseFloat(upper);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  return `${lo.toFixed(0)}–${hi.toFixed(0)}`;
};

const getInRangeLabel = (row, actualValue = null) => {
  const actual = actualValue != null ? parseFloat(actualValue) : parseFloat(row?.actual_qty ?? 0);
  if (!Number.isFinite(actual) || actual <= 0) return '—';
  if (row?.in_range != null && row.in_range !== '') {
    const v = Number(row.in_range);
    if (Number.isFinite(v)) return v === 1 ? 'Yes' : 'No';
  }
  const { lower, upper } = getForecastBandFields(row);
  const lo = parseFloat(lower);
  const hi = parseFloat(upper);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return '—';
  return actual >= lo && actual <= hi ? 'Yes' : 'No';
};

const getInRangeChipColor = (label) => {
  if (label === 'Yes') return 'success';
  if (label === 'No') return 'error';
  return 'default';
};

const hasSoldDay = (row) => {
  const actual = parseFloat(row?.actual_qty ?? 0);
  return Number.isFinite(actual) && actual > 0;
};

/** Parse HH:MM / HH:MM:SS to minutes from midnight for session ordering. */
const sessionStartMinutes = (timeStr) => {
  if (!timeStr && timeStr !== 0) return Number.POSITIVE_INFINITY;
  const parts = String(timeStr).trim().split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return Number.POSITIVE_INFINITY;
  return h * 60 + m;
};

/**
 * Unique session types that had actual sales for this item, ordered by session start time.
 */
const getSoldSessionTypesByTime = (sessions) => {
  const meta = new Map();
  (sessions || []).forEach((s) => {
    const type = s?.session_type;
    if (!type || type === 'All Sessions') return;
    if (!meta.has(type)) {
      meta.set(type, {
        session_type: type,
        start: s.session_start_time,
        sold: false,
      });
    }
    const entry = meta.get(type);
    if (hasSoldDay(s)) entry.sold = true;
    const startMins = sessionStartMinutes(s.session_start_time);
    if (startMins < sessionStartMinutes(entry.start)) {
      entry.start = s.session_start_time;
    }
  });
  return [...meta.values()]
    .filter((m) => m.sold)
    .sort((a, b) => {
      const diff = sessionStartMinutes(a.start) - sessionStartMinutes(b.start);
      if (diff !== 0) return diff;
      return String(a.session_type).localeCompare(String(b.session_type));
    })
    .map((m) => m.session_type);
};

const resolveInRangePct = (itemData, strictRows = []) => {
  const soldRows = strictRows.filter(hasSoldDay);
  if (!soldRows.length) return null;
  const hits = soldRows.filter((r) => getInRangeLabel(r, r.actual_qty) === 'Yes').length;
  return hits / soldRows.length;
};

const formatFcDisplayName = (fcId, fcMap = {}, options = []) => {
  if (!fcId) return '';
  if (fcMap[fcId]) return fcMap[fcId];
  const fromList = options.find((fc) => fc.id === fcId);
  if (fromList?.name && fromList.name !== fcId) return fromList.name;
  return fcId;
};

const formatRestaurantDisplayName = (restId, restMap = {}, options = []) => {
  if (!restId) return '';
  if (restMap[restId]) return restMap[restId];
  const fromList = options.find((rest) => rest.id === restId);
  if (fromList?.name && fromList.name !== restId) return fromList.name;
  return restId;
};

// Hardcoded foodcourt name mapping
const hardcodedFcMapping = {
  '6051d1f64ec6952ca5b45dba': 'CG-Ecospace 6B-BLR',
  '60f172e2d1b6e328e744cf65': 'CG-Cyber Towers-CHN',
  '61e39f4b2acc405ba5c12202': 'CG-S82-NCR-NOI',
  '62720dcd1410b643ccc5eaf0': 'CG-DV B4-BLR',
  '6344e94b2db92732f3849cd4': 'S&P Global-Prahlad Nagar-AHD',
  '651ba1552d0b1d001b6c2e53': 'Air India-Santacruz-MUM',
  '65b48ebd8d7d8f001be02f79': 'Air India-Aviation Academy-GUR',
  '66751bbe191e5a001af454d4': 'Sodexo-CG GAR-HYD',
  '66751c8e3f015e001b2ed8b4': 'Sodexo-CG-Campus-HYD',
  '66ffbf790724f300133f7c22': 'CG Technopolis- Sec 5- KOL'
};

// Restaurant mapping is now loaded from fcr_mapping.csv via API

// Mapping of reason codes to business-facing reasons
const reasonCodeMapping = {
  'INSUFFICIENT_HISTORY': 'Limited and inconsistent historical sales',
  'LOW_SCALE_DEMAND': 'Low overall demand base',
  'VOLATILE_DEMAND': 'Highly unpredictable demand pattern',
  'HISTORICAL_SPIKE': 'One-off sales spike distorting historical trend',
  'POST_SPIKE': 'Unexpected demand surge in validation period',
  'POST_DROP': 'Demand decline compared to historical trend',
  'POST_DAY_SPIKE': 'One-day anomaly impacting validation results'
};

// Helper function to convert text to proper case
const toProperCase = (text) => {
  if (!text) return '';
  return String(text)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to convert reason code to business-facing reason
const convertReasonCode = (reasonCode) => {
  if (!reasonCode) return null;
  const trimmed = String(reasonCode).trim();
  const reason = reasonCodeMapping[trimmed] || 'Unknown';
  return toProperCase(reason);
};

// Custom tooltip component for daily sales chart
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Check if only mean line is active
  const onlyMean = payload.length === 1 && payload[0].dataKey === 'mean';
  
  if (onlyMean) {
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '14px'
      }}>
        <p style={{ margin: 0, fontWeight: 600 }}>
          {payload[0].value.toFixed(1)}
        </p>
      </div>
    );
  }

  const quantityPayload = payload.find(p => p.dataKey === 'quantity');
  const forecastPayload = payload.find(p => p.dataKey === 'forecast_plot');
  if (quantityPayload || forecastPayload) {
    const dateObj = new Date(label);
    const dateStr = dateObj.toLocaleDateString();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOfWeek = dayNames[dateObj.getDay()];
    
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '8px',
        fontSize: '14px',
        maxWidth: '200px',
        whiteSpace: 'pre-line'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 600 }}>
          Date: {dateStr} ({dayOfWeek})
        </p>
        {quantityPayload && (
          <p style={{ margin: '0 0 4px 0' }}>
            Sales Quantity: {quantityPayload.value}
          </p>
        )}
        {forecastPayload && (
          <p style={{ margin: '0 0 4px 0', color: '#757575' }}>
            Forecast: {forecastPayload.value}
          </p>
        )}
      </div>
    );
  }

  return null;
};

// Helper function to format reasons text with business-facing descriptions
const formatReasonsText = (reasons_pre, reasons_post, status) => {
  // For Green status, return "No Performance Issues Detected"
  if (status === 'Green') {
    return 'No Performance Issues Detected';
  }
  
  const parts = [];
  
  if (reasons_pre) {
    const preReasons = String(reasons_pre).split(';').map(r => convertReasonCode(r.trim())).filter(r => r);
    if (preReasons.length > 0) {
      parts.push(`Pre Period - ${preReasons.join(' | ')}`);
    }
  }
  
  if (reasons_post) {
    const postReasons = String(reasons_post).split(';').map(r => convertReasonCode(r.trim())).filter(r => r);
    if (postReasons.length > 0) {
      parts.push(`Post Period - ${postReasons.join(' | ')}`);
    }
  }
  
  return parts.length > 0 ? parts.join('\n') : null;
};

const PRE_REASON_GROUPS = [
  {
    label: 'Insufficient Data – Model Stability Risk',
    codes: ['SPARSE_DATA_FLAG', 'INSUFFICIENT_HISTORY_FLAG']
  },
  {
    label: 'Low Scale or Volatile Demand',
    codes: ['LOW_SCALE_DEMAND_FLAG', 'VOLATILE_DEMAND_FLAG']
  },
  {
    label: 'Recent Sales Spike – Prediction Bias Risk',
    codes: ['SPIKE_IN_RECENT_PRE_WEEKS_FLAG']
  },
  {
    label: 'Recent Sales Drop – Prediction Bias Risk',
    codes: ['DROP_IN_RECENT_PRE_WEEKS_FLAG']
  }
];

const parseReasonList = (reasonString) => {
  if (!reasonString) return [];
  return String(reasonString)
    .split(';')
    .map(entry => entry.trim())
    .filter(Boolean)
    .map(entry => entry.toUpperCase());
};

const buildPreReasonEntries = (reasonsPre) => {
  const normalized = parseReasonList(reasonsPre);
  if (!normalized.length) return [];
  const reasonSet = new Set(normalized);
  return PRE_REASON_GROUPS
    .filter(group => group.codes.some(code => reasonSet.has(code)))
    .map(group => group.label);
};

const DAY_NAME_OPTIONS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// Only include weekdays (Monday-Friday) for day-of-week spike/drop flags, exclude Saturday and Sunday
const WEEKDAY_NAMES = new Set(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']);

// If accuracy >= 90%, day-of-week spike/drop flags are filtered out
const buildPostReasonEntries = (reasonsPost, accuracy = null) => {
  const normalized = parseReasonList(reasonsPost);
  if (!normalized.length) return [];

  const reasonSet = new Set(normalized);
  const entries = [];
  const hasOverallSpike = reasonSet.has('POST_SPIKE_FLAG');
  const hasOverallDrop = reasonSet.has('POST_DROP_FLAG');

  // Removed redundant "Overall Weekly Sales Deviation" header
  if (hasOverallSpike) {
    entries.push('Overall Spike in Sales Across the Week');
  }
  if (hasOverallDrop) {
    entries.push('Overall Drop in Sales Across the Week');
  }

  // Calculate accuracy percentage if MAPE is provided
  // MAPE is stored as decimal (0-1), so accuracy = (1 - MAPE) * 100
  let accuracyPct = null;
  if (accuracy !== null && accuracy !== undefined) {
    const accuracyNum = typeof accuracy === 'number' ? accuracy : parseFloat(accuracy);
    if (!isNaN(accuracyNum) && isFinite(accuracyNum)) {
      // MAPE is stored as decimal (0-1), convert to accuracy percentage
      // If MAPE = 0.1 (10% error), then accuracy = (1 - 0.1) * 100 = 90%
      accuracyPct = (1 - accuracyNum) * 100;
    }
  }

  // Check if we should filter day-of-week flags (accuracy >= 90%)
  const shouldFilterDayFlags = accuracyPct !== null && accuracyPct >= 90;

  // Only add day-of-week flags if accuracy < 90%
  if (!shouldFilterDayFlags) {
    normalized.forEach(reason => {
      if (reason.startsWith('SPIKE_')) {
        const dayName = reason.slice(6);
        if (WEEKDAY_NAMES.has(dayName)) {
          entries.push(`Spike over Recent ${toProperCase(dayName)}s`);
        }
      }
      if (reason.startsWith('DROP_')) {
        const dayName = reason.slice(5);
        if (WEEKDAY_NAMES.has(dayName)) {
          entries.push(`Drop over Recent ${toProperCase(dayName)}s`);
        }
      }
    });
  }

  // Check for operational issue flags
  // Handle "Potential Minor Issue - {Day}" → "Potential Ops Issue (Minor) - {Day}"
  // Handle "Potential Ops Issue - {Day}" → "Potential Ops Issue - {Day}"
  const dayNames = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  dayNames.forEach(dayUpper => {
    const dayProper = toProperCase(dayUpper);
    // Check for "POTENTIAL MINOR ISSUE - {DAY}"
    if (normalized.some(r => r.includes(`POTENTIAL MINOR ISSUE - ${dayUpper}`))) {
      entries.push(`Potential Ops Issue (Minor) - ${dayProper}`);
    }
    // Check for "POTENTIAL OPS ISSUE - {DAY}" (without Minor)
    else if (normalized.some(r => {
      return r.includes(`POTENTIAL OPS ISSUE - ${dayUpper}`) && !r.includes('MINOR');
    })) {
      entries.push(`Potential Ops Issue - ${dayProper}`);
    }
  });

  return entries;
};

const ReasonTooltipContent = ({ status, preReasons, postReasons, fallbackReason }) => {
  const hasPre = preReasons && preReasons.length > 0;
  const hasPost = postReasons && postReasons.length > 0;

  if (!hasPre && !hasPost) {
    const message = fallbackReason || (status === 'Green' ? 'No Performance Issues Detected' : 'No Specific Reasons Found');
    return (
      <Typography sx={{ fontSize: '0.8rem', whiteSpace: 'pre-line' }}>
        {message}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, minWidth: 300 }}>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', mb: 0.5 }}>
          FRI – Past Sales Related
        </Typography>
        {hasPre ? (
          preReasons.map(reason => (
            <Typography key={`pre-${reason}`} sx={{ fontSize: '0.75rem', whiteSpace: 'normal' }}>
              {reason}
            </Typography>
          ))
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>None</Typography>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', mb: 0.5 }}>
          Current Week Anomalies
        </Typography>
        {hasPost ? (
          postReasons.map(reason => (
            <Typography key={`post-${reason}`} sx={{ fontSize: '0.75rem', whiteSpace: 'normal' }}>
              {reason}
            </Typography>
          ))
        ) : (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>None</Typography>
        )}
      </Box>
    </Box>
  );
};

function RestaurantLevel({ onItemClick, initialFilters, onFiltersChange }) {
  const [strictData, setStrictData] = useState([]);
  const [sessionData, setSessionData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { folderInfo, selectedFolder, refreshing, loading: folderLoading } = useFolder();
  
  // Clear data when refreshing starts
  useEffect(() => {
    if (refreshing) {
      setStrictData([]);
      setError(null);
    }
  }, [refreshing]);
  
  const [foodcourts, setFoodcourts] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  
  // Restore selections from localStorage on mount
  const getInitialFC = () => {
    if (initialFilters?.foodcourtid) return initialFilters.foodcourtid;
    const saved = localStorage.getItem('selectedFC');
    return saved || '6051d1f64ec6952ca5b45dba'; // Default: CG-Ecospace 6B-BLR
  };
  
  const getInitialRestaurant = () => {
    if (initialFilters?.restaurant) return initialFilters.restaurant;
    const saved = localStorage.getItem('selectedRestaurant');
    return saved || '6053268e4ec6952ca5b46330'; // Default: SLV Enterprices (Veg Meal And Dosa)
  };
  
  const [selectedFC, setSelectedFC] = useState(getInitialFC);
  const [selectedRestaurant, setSelectedRestaurant] = useState(getInitialRestaurant);
  const [collapsedSessions, setCollapsedSessions] = useState({}); // Track collapsed sessions: { "itemname_sessionIdx": true }
  const [fcMapping, setFcMapping] = useState({});
  const [restMapping, setRestMapping] = useState({});
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [combosOnly, setCombosOnly] = useState(false);
  const itemsPerPage = 15;
  const [aggregatedData, setAggregatedData] = useState([]);
  const [validationSummaryData, setValidationSummaryData] = useState([]);
  const { debugMode } = useFolder();
  
  // Daily Sales Chart Dialog State
  const [dailySalesDialogOpen, setDailySalesDialogOpen] = useState(false);
  const [dailySalesData, setDailySalesData] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [timePeriod, setTimePeriod] = useState('6months');
  const [showNonZeroOnly, setShowNonZeroOnly] = useState(false);
  const [combosOnlyChart, setCombosOnlyChart] = useState(false);
  const [selectedDays, setSelectedDays] = useState(DAY_NAME_OPTIONS.slice(1, 6)); // default: Mon-Fri
  const [dateRange, setDateRange] = useState([null, null]); // [startDate, endDate] for date range slider
const ALL_DAYS_VALUE = '__ALL__';

  // Initialize date range slider with default values based on time period
  useEffect(() => {
    const timePeriodConfig = {
      '1month': { days: 28 },
      '3months': { days: 91 },
      '6months': { days: 182 },
      '1year': { days: 364 },
      '2years': { days: 730 }
    };
    const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
    
    let modelDateObj = null;
    if (folderInfo?.modelDate) {
      try {
        const year = folderInfo.modelDate.substring(0, 4);
        const month = folderInfo.modelDate.substring(4, 6);
        const day = folderInfo.modelDate.substring(6, 8);
        modelDateObj = new Date(`${year}-${month}-${day}`);
      } catch (e) {
        console.error('Error parsing model date from folderInfo:', e);
      }
    }
    if (!modelDateObj && selectedFolder) {
      try {
        const parts = selectedFolder.split('_');
        if (parts.length >= 1) {
          const modelDateStr = parts[0];
          const year = modelDateStr.substring(0, 4);
          const month = modelDateStr.substring(4, 6);
          const day = modelDateStr.substring(6, 8);
          modelDateObj = new Date(`${year}-${month}-${day}`);
        }
      } catch (e) {
        console.error('Error parsing model date from selectedFolder:', e);
      }
    }
    
    if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
      const defaultStart = new Date(modelDateObj);
      defaultStart.setDate(defaultStart.getDate() - selectedConfig.days);
      const defaultEnd = new Date(modelDateObj);
      defaultEnd.setDate(defaultEnd.getDate() + 7);
      setDateRange([defaultStart, defaultEnd]);
    }
  }, [folderInfo, selectedFolder, timePeriod]);

  const handleDayFilterChange = (event) => {
    const value = event.target.value;
    const rawValues = typeof value === 'string' ? value.split(',') : value;
    if (rawValues.includes(ALL_DAYS_VALUE)) {
      if (selectedDays.length === DAY_NAME_OPTIONS.length) {
        setSelectedDays([]);
        return;
      }
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    const effectiveValues = rawValues.filter((item) => item !== ALL_DAYS_VALUE);
    if (effectiveValues.length === DAY_NAME_OPTIONS.length) {
      setSelectedDays(DAY_NAME_OPTIONS);
      return;
    }
    setSelectedDays(effectiveValues);
  };

  // Run date on/after the new-model cutoff (see utils/modelFormat.js)
  const isNewModelFormat = useMemo(
    () => isNewModelFormatFolder(selectedFolder),
    [selectedFolder]
  );

  // When Predict Mode is on: show only forecast columns (no actuals, no accuracy/status). Normal mode = full table as before.
  const showOnlyForecasts = debugMode;

  const loadData = useCallback(async () => {
    try {
      console.log('RestaurantLevel: Loading data (folder:', selectedFolder || 'server default', ')');
      setLoading(true);
      setError(null);
      
      // Wait a bit if refreshing to ensure server has loaded the new folder data
      if (refreshing) {
        console.log('RestaurantLevel: Waiting for refresh to complete...');
        await new Promise(resolve => setTimeout(resolve, 600));
      }
      
      console.log('RestaurantLevel: Fetching data from API...');
      const [strictRes, fcRes, fcMappingRes, aggregatedRes, validationRes] = await Promise.all([
        axios.get('/api/strict').catch(err => {
          console.error('Error fetching strict data:', err.response?.status, err.response?.data, err.message);
          if (err.response?.status === 503) {
            throw new Error('Data not loaded yet. Please wait a moment and try again.');
          }
          throw err;
        }),
        axios.get('/api/foodcourts').catch(err => {
          console.warn('Error fetching foodcourts:', err.response?.status, err.message);
          return { data: [] };
        }),
        axios.get('/api/fc-mapping').catch(err => {
          console.warn('Error fetching FC mapping:', err.response?.status, err.message);
          return { data: [] };
        }),
        axios.get('/api/aggregated').catch(err => {
          console.warn('Error fetching aggregated data:', err.response?.status, err.message);
          return { data: [] };
        }),
        axios.get('/api/validation-summary').catch(err => {
          console.warn('Error fetching validation summary:', err.response?.status, err.message);
          return { data: [] };
        })
      ]);
      
      console.log('RestaurantLevel: Received data - strict:', strictRes?.data?.length || 0, 'foodcourts:', fcRes?.data?.length || 0);
      
      if (!strictRes.data || strictRes.data.length === 0) {
        console.warn('RestaurantLevel: No strict data received');
        setError('No forecast data available for the selected folder.');
        setStrictData([]);
        setFoodcourts([]);
        return;
      }
      
      setStrictData(filterAllowedItems(strictRes.data || []));
      setAggregatedData(filterAllowedItems(aggregatedRes.data || []));
      setValidationSummaryData(filterAllowedItems(validationRes.data || []));
      
      // Build FC mapping from API (fc_mapping.csv) - priority 1
      const fcMap = {};
      if (fcMappingRes.data && fcMappingRes.data.length > 0) {
        fcMappingRes.data.forEach(row => {
          if (row.foodcourtid && row.foodcourtname) {
            fcMap[row.foodcourtid] = row.foodcourtname;
          }
        });
      }
      
      // Fallback to /api/foodcourts endpoint - priority 2
      if (fcRes.data && fcRes.data.length > 0) {
        fcRes.data.forEach(fc => {
          if (fc.id && fc.name && !fcMap[fc.id]) {
            fcMap[fc.id] = fc.name;
          }
        });
      }
      
      // Hardcoded as last fallback - priority 3
      Object.keys(hardcodedFcMapping).forEach(fcId => {
        if (!fcMap[fcId]) {
          fcMap[fcId] = hardcodedFcMapping[fcId];
        }
      });
      
      setFcMapping(fcMap);
      
      // Get food courts that have data in aggregatedData or strictData
      const foodcourtsWithData = new Set();
      if (aggregatedRes.data && aggregatedRes.data.length > 0) {
        aggregatedRes.data.forEach(row => {
          if (row.foodcourtid) {
            foodcourtsWithData.add(row.foodcourtid);
          }
        });
      }
      if (strictRes.data && strictRes.data.length > 0) {
        strictRes.data.forEach(row => {
          if (row.foodcourtid) {
            foodcourtsWithData.add(row.foodcourtid);
          }
        });
      }
      
      // Only show food courts that have data for the selected run/model week
      const fcs = (fcRes.data || [])
        .filter(fc => fc.id && fc.name && foodcourtsWithData.has(fc.id))
        .map(fc => ({
          id: fc.id,
          name: formatFcDisplayName(fc.id, fcMap, fcRes.data || []),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFoodcourts(fcs);
      
      setError(null);
      console.log('RestaurantLevel: Data loaded successfully');
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Unknown error';
      setError(errorMsg);
      console.error('RestaurantLevel: Error loading data:', errorMsg, err);
      setStrictData([]);
      setFoodcourts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, refreshing]); // selectedFolder: daily-sales etc.; strict/aggregated use server cache

  const loadRestaurants = useCallback(async (fcId) => {
    if (!fcId) {
      setRestaurants([]);
      return;
    }
    try {
      const response = await axios.get(`/api/restaurants?foodcourtid=${fcId}`);
      
      // Build restaurant mapping from API response (which uses fcr_mapping.csv)
      const restMap = {};
      response.data.forEach(rest => {
        if (rest.id && rest.name) {
          restMap[rest.id] = rest.name;
        }
      });
      setRestMapping(restMap);
      
      // Get restaurants that have data in aggregatedData or strictData
      const restaurantsWithData = new Set();
      const restaurantFriCounts = {}; // Count FRIs per restaurant
      
      if (aggregatedData && aggregatedData.length > 0) {
        aggregatedData.forEach(row => {
          if (row.foodcourtid === fcId && row.restaurant) {
            restaurantsWithData.add(row.restaurant);
            restaurantFriCounts[row.restaurant] = (restaurantFriCounts[row.restaurant] || 0) + 1;
          }
        });
      }
      if (strictData && strictData.length > 0) {
        strictData.forEach(row => {
          if (row.foodcourtid === fcId && row.restaurant) {
            restaurantsWithData.add(row.restaurant);
            // Only count if not already counted in aggregatedData
            if (!aggregatedData || aggregatedData.length === 0) {
              restaurantFriCounts[row.restaurant] = (restaurantFriCounts[row.restaurant] || 0) + 1;
            }
          }
        });
      }
      
      // Only show restaurants that have data for the selected food court
      const rests = response.data
        .filter(rest => rest.id && rest.name && restaurantsWithData.has(rest.id))
        .map(rest => ({
          id: rest.id,
          name: formatRestaurantDisplayName(rest.id, restMap, response.data || []),
          numFris: restaurantFriCounts[rest.id] || 0
        }))
        .sort((a, b) => {
          // Sort by FRI count (descending), then alphabetically if same count
          if (b.numFris !== a.numFris) {
            return b.numFris - a.numFris;
          }
          return a.name.localeCompare(b.name);
        });
      setRestaurants(rests);
      
      // Set default restaurant to the one with the most FRIs (first in sorted list)
      // Only set if no restaurant is currently selected or if the selected one doesn't exist in the new list
      // This will be overridden by savedSelections or initialFilters if they exist (handled in separate useEffect)
      if (rests.length > 0) {
        const currentRestExists = rests.some(rest => rest.id === selectedRestaurant);
        if (!selectedRestaurant || !currentRestExists) {
          const restaurantWithMostFris = rests.find(rest => rest.numFris > 0) || rests[0];
          if (restaurantWithMostFris) {
            setSelectedRestaurant(restaurantWithMostFris.id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading restaurants:', err);
      setRestaurants([]);
    }
  }, [aggregatedData, strictData]);

  const loadSessionData = useCallback(async (itemname) => {
    if (!selectedFC || !selectedRestaurant || !itemname) return;
    
    const cacheKey = `${selectedFC}_${selectedRestaurant}_${itemname}`;
    
    try {
      const response = await axios.get('/api/session/strict', {
        params: {
          foodcourtid: selectedFC,
          restaurant: selectedRestaurant,
          itemname: itemname
        }
      });
      
      setSessionData(prev => {
        // Don't reload if already cached
        if (prev[cacheKey]) return prev;
        return {
          ...prev,
          [cacheKey]: response.data
        };
      });
    } catch (err) {
      console.error('Error loading session data:', err);
    }
  }, [selectedFC, selectedRestaurant]);

  // Load session data for all items when FC and Restaurant are selected
  useEffect(() => {
    if (selectedFC && selectedRestaurant && strictData.length > 0) {
      const itemnames = [...new Set(strictData
        .filter(row => row.foodcourtid === selectedFC && row.restaurant === selectedRestaurant)
        .map(row => row.itemname))];
      
      itemnames.forEach(itemname => {
        const cacheKey = `${selectedFC}_${selectedRestaurant}_${itemname}`;
        // Check if already loaded before loading
        if (!sessionData[cacheKey]) {
          loadSessionData(itemname);
        }
      });
    }
  }, [selectedFC, selectedRestaurant, strictData, sessionData, loadSessionData]);

  useEffect(() => {
    console.log('RestaurantLevel: useEffect triggered - selectedFolder:', selectedFolder, 'refreshing:', refreshing, 'folderLoading:', folderLoading);
    if (!folderLoading && !refreshing) {
      console.log('RestaurantLevel: Calling loadData (strict/aggregated from server cache)');
      loadData();
    } else {
      console.log('RestaurantLevel: Skipping loadData - folderLoading:', folderLoading, 'refreshing:', refreshing);
    }
  }, [selectedFolder, folderLoading, refreshing, loadData]);

  // If the saved FC is not in the current data set, pick the first available one
  useEffect(() => {
    if (foodcourts.length > 0 && !foodcourts.some((fc) => fc.id === selectedFC)) {
      setSelectedFC(foodcourts[0].id);
    }
  }, [foodcourts, selectedFC]);

  // Restore filters when initialFilters changes (e.g., when navigating back)
  useEffect(() => {
    if (initialFilters && initialFilters.foodcourtid && initialFilters.restaurant) {
      console.log('Restoring filters:', initialFilters);
      // Always restore filters when initialFilters is provided (coming back from ItemDetail)
      setSelectedFC(initialFilters.foodcourtid);
      // Note: restaurant will be set after restaurants are loaded for this FC
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters?.foodcourtid]);

  // Set restaurant after restaurants are loaded for the selected FC
  useEffect(() => {
    if (initialFilters && initialFilters.restaurant && selectedFC === initialFilters.foodcourtid && restaurants.length > 0) {
      // Check if the restaurant exists in the list
      const restaurantExists = restaurants.some(r => r.id === initialFilters.restaurant);
      if (restaurantExists && selectedRestaurant !== initialFilters.restaurant) {
        console.log('Restoring restaurant:', initialFilters.restaurant);
        setSelectedRestaurant(initialFilters.restaurant);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurants, initialFilters?.restaurant, selectedFC]);

  useEffect(() => {
    if (selectedFC) {
      loadRestaurants(selectedFC);
      setPage(1); // Reset to first page when FC changes
      setSearchQuery(''); // Reset search when FC changes
      // Reset restaurant when FC changes to a different one (preserve default on initial load)
      if (selectedFC !== '6051d1f64ec6952ca5b45dba') {
        setSelectedRestaurant('');
      }
    } else {
      setRestaurants([]);
      setSelectedRestaurant('');
      setPage(1);
      setSearchQuery('');
    }
  }, [selectedFC, loadRestaurants, aggregatedData, strictData]);
  
  // Persist selectedFC to localStorage
  useEffect(() => {
    if (selectedFC) {
      localStorage.setItem('selectedFC', selectedFC);
    }
  }, [selectedFC]);
  
  // Persist selectedRestaurant to localStorage
  useEffect(() => {
    if (selectedRestaurant) {
      localStorage.setItem('selectedRestaurant', selectedRestaurant);
    }
  }, [selectedRestaurant]);
  
  // Clear selectedRestaurant when selectedFC changes to trigger default selection in loadRestaurants
  useEffect(() => {
    if (selectedFC) {
      setSelectedRestaurant(null); // Clear to trigger default selection logic in loadRestaurants
    }
  }, [selectedFC]);

  useEffect(() => {
    setPage(1); // Reset to first page when restaurant changes
    setSearchQuery(''); // Reset search when restaurant changes
    setCombosOnly(false); // Reset combos filter when restaurant changes
  }, [selectedRestaurant]);

  // Helper function to check if an item is a combo (defined early to avoid initialization issues)
  const isComboItem = useCallback((itemname) => {
    if (!itemname) return false;
    const lowerName = itemname.toLowerCase();
    const comboKeywords = ['combo', 'combos', 'thali', 'meal', 'meals', 'biriyani', 'biriani', 'biryani', 'lunch', 'breakfast', 'dinner'];
    const hasComboKeyword = comboKeywords.some(keyword => lowerName.includes(keyword));
    const hasWithPattern = lowerName.includes(' with ');
    return hasComboKeyword || hasWithPattern;
  }, []);

  // Load restaurant-level daily sales data
  const loadRestaurantDailySales = useCallback(async () => {
    if (!selectedFC || !selectedRestaurant || !selectedFolder) {
      return;
    }
    
    setLoadingSales(true);
    setSalesError(null);
    try {
      // Get combo itemnames if combos filter is enabled
      let itemnamesParam = null;
      if (combosOnlyChart) {
        // Get unique itemnames from strictData for this restaurant
        const restaurantItems = strictData
          .filter(row => row.foodcourtid === selectedFC && row.restaurant === selectedRestaurant)
          .map(row => row.itemname)
          .filter((itemname, index, self) => self.indexOf(itemname) === index); // Get unique itemnames
        
        const comboItems = restaurantItems
          .filter(itemname => isComboItem(itemname));
        
        if (comboItems.length > 0) {
          itemnamesParam = comboItems.join(',');
        } else {
          // No combo items found, return empty data
          setDailySalesData([]);
          setLoadingSales(false);
          return;
        }
      }
      
      const params = {
        foodcourtid: selectedFC,
        restaurant: selectedRestaurant,
        folder: selectedFolder
      };
      if (itemnamesParam) {
        params.itemnames = itemnamesParam;
      }
      
      const response = await axios.get('/api/restaurant-daily-sales', { params });
      const data = response.data || [];
        const last730Days = data.slice(-730);
        setDailySalesData(last730Days);
    } catch (err) {
      setSalesError(err.message);
      console.error('Error loading restaurant daily sales:', err);
    } finally {
      setLoadingSales(false);
    }
  }, [selectedFC, selectedRestaurant, selectedFolder, combosOnlyChart, strictData, isComboItem]);

  // Load data when dialog opens or filters change
  useEffect(() => {
    if (dailySalesDialogOpen && selectedFC && selectedRestaurant && selectedFolder) {
      loadRestaurantDailySales();
    }
  }, [dailySalesDialogOpen, selectedFC, selectedRestaurant, selectedFolder, combosOnlyChart, loadRestaurantDailySales]);

  const toggleSessionRow = (e, itemname, sessionIdx) => {
    e.stopPropagation(); // Prevent item click when clicking expand icon
    const key = `${itemname}_${sessionIdx}`;
    setCollapsedSessions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleAllSessionsForItem = (e, itemname, sessionRows) => {
    e.stopPropagation(); // Prevent item click when clicking expand icon
    // Check if all non-"All Sessions" rows are collapsed
    const nonAllSessions = sessionRows.slice(1); // Skip "All Sessions" (index 0)
    const allCollapsed = nonAllSessions.every((_, idx) => {
      const key = `${itemname}_${idx + 1}`;
      return collapsedSessions[key] === true;
    });
    
    // Toggle all non-"All Sessions" rows
    const updates = {};
    nonAllSessions.forEach((_, idx) => {
      const key = `${itemname}_${idx + 1}`;
      updates[key] = allCollapsed ? false : true; // If all collapsed, expand all; else collapse all
    });
    
    setCollapsedSessions(prev => ({
      ...prev,
      ...updates
    }));
  };

  const handleItemNameClick = (e, item) => {
    e.stopPropagation(); // Prevent any parent handlers
    console.log('Item clicked:', item.itemname);
    
    if (!onItemClick) {
      console.warn('onItemClick handler not provided');
      return;
    }
    
      const filters = {
        foodcourtid: selectedFC,
        restaurant: selectedRestaurant
      };
      if (onFiltersChange) {
        onFiltersChange(filters);
      }

      // Non Veg Lunch aggregate: pass sum-level metrics (no row in aggregated CSV)
      if (isComboAggregateItem(item.itemname) || item.is_combo_aggregate) {
        const aggRows = buildComboAggregateStrictRows(strictData, selectedFC, selectedRestaurant);
        const summary = summarizeComboAggregateMetrics(aggRows);
        const fullItemData = {
          foodcourtid: selectedFC,
          restaurant: selectedRestaurant,
          itemname: COMBO_AGGREGATE_ITEMNAME,
          is_combo_aggregate: true,
          num_days: summary.num_days,
          avg_daily_units_sold: summary.avg_daily_units_sold,
          avg_mape_component_model: summary.mape,
          avg_mad_component_model: summary.mad,
          APE_component_model: summary.mape,
          MAD_component_model: summary.mad,
          status_component_model: summary.status,
        };
        onItemClick(fullItemData, filters);
        return;
      }

      // Find the item data from strictData to pass to DeepDive
      const itemData = strictData.find(row => 
        row.foodcourtid === selectedFC && 
        row.restaurant === selectedRestaurant && 
        row.itemname === item.itemname
      );
      
      if (itemData) {
        // Get aggregated data for this item
        axios.get('/api/aggregated').then(response => {
          const aggregatedItem = response.data.find(row =>
            row.foodcourtid === selectedFC &&
            row.restaurant === selectedRestaurant &&
            row.itemname === item.itemname
          );
          
          const fullItemData = aggregatedItem || itemData;
        console.log('Calling onItemClick with:', fullItemData);
        onItemClick(fullItemData, filters);
      }).catch((err) => {
        console.error('Error fetching aggregated data:', err);
          // Fallback to itemData if aggregated fetch fails
        console.log('Calling onItemClick with fallback:', itemData);
        onItemClick(itemData, filters);
      });
    } else {
      // If itemData not found in strictData, try to construct minimal data from item
      console.warn('Item data not found in strictData, using item object');
      const minimalItemData = {
        foodcourtid: selectedFC,
        restaurant: selectedRestaurant,
        itemname: item.itemname
      };
      console.log('Calling onItemClick with minimal data:', minimalItemData);
      onItemClick(minimalItemData, filters);
    }
  };

  // Helper function to proper case text
  const properCase = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to get item metrics (MAPE, MAD, Status) from aggregated data
  const getItemMetrics = (itemname, strictRows = []) => {
    // Non Veg Lunch (= Combo 1 + Combo 2): compute accuracy on summed actual vs forecast
    if (isComboAggregateItem(itemname)) {
      const dayRows =
        strictRows && strictRows.length > 0
          ? strictRows
          : buildComboAggregateStrictRows(strictData, selectedFC, selectedRestaurant);
      const summary = summarizeComboAggregateMetrics(dayRows);
      return {
        mape: summary.mape,
        mad: summary.mad,
        status: summary.status,
        badForecastReason: null,
        preReasonEntries: [],
        postReasonEntries: [],
        inRangePct: summary.inRangePct,
      };
    }

    const itemData = aggregatedData.find(row =>
      row.foodcourtid === selectedFC &&
      row.restaurant === selectedRestaurant &&
      row.itemname === itemname
    );
    
    if (!itemData) return { mape: null, mad: null, status: null, badForecastReason: null, inRangePct: null };
    
    // Always use New Model (component_model) for MAPE, MAD, and Status
    const mape = parseFloat(itemData.avg_mape_component_model || itemData.APE_component_model || itemData.avg_mape || 0) || null;
    const mad = parseFloat(itemData.avg_mad_component_model || itemData.MAD_component_model || itemData.avg_mad || 0) || null;
    const status = itemData.status_component_model;
    const inRangePct = resolveInRangePct(itemData, strictRows);
    
    // Get reasons from aggregated data first (reasons_pre and reasons_post)
    let badForecastReason = formatReasonsText(itemData.reasons_pre, itemData.reasons_post, status);
    const preReasonEntries = buildPreReasonEntries(itemData.reasons_pre);
    // Pass mape to filter day-of-week flags if accuracy >= 90%
    const postReasonEntries = buildPostReasonEntries(itemData.reasons_post, mape);
    
    // If no reasons from formatReasonsText, check validation summary (using bad_result_reason to match ItemDetail)
    if (!badForecastReason && status !== 'Green') {
      const validation = validationSummaryData.find(vs =>
        vs.foodcourtid === selectedFC &&
        vs.restaurant === selectedRestaurant &&
        vs.itemname === itemname
      );
      if (validation?.bad_result_reason) {
        badForecastReason = toProperCase(validation.bad_result_reason);
      }
    }
    
    return { mape, mad, status, badForecastReason, preReasonEntries, postReasonEntries, inRangePct };
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Green':
        return 'success';
      case 'Orange':
        return 'warning';
      case 'Red':
        return 'error';
      default:
        return 'default';
    }
  };

  // Helper function to calculate MAPE for a day (if actual > 0)
  // Returns MAPE as a decimal (0-1), not percentage
  const calculateDayMAPE = (actual, forecast) => {
    if (!actual || actual === 0) return null;
    if (forecast === null || forecast === undefined) return null;
    const error = Math.abs(actual - forecast) / actual;
    return Math.min(error, 1); // Cap at 1, return as decimal (0-1)
  };

  // Helper function to calculate MAD for a day (if actual > 0)
  const calculateDayMAD = (actual, forecast) => {
    if (!actual || actual === 0) return null;
    if (forecast === null || forecast === undefined) return null;
    return Math.abs(actual - forecast);
  };

  // Process data to create table structure
  const processTableData = () => {
    if (!selectedFC || !selectedRestaurant) return { items: [], days: [] };
    
    // Filter data by selected FC and Restaurant
    const filtered = strictData.filter(row => 
      row.foodcourtid === selectedFC && row.restaurant === selectedRestaurant
    );
    
    if (filtered.length === 0) return { items: [], days: [] };
    
    // Get unique dates and sort them - these will be our day columns
    const dates = [...new Set(filtered.map(row => row.date))].sort((a, b) => 
      new Date(a) - new Date(b)
    );
    
    // Get unique itemnames
    const itemnames = [...new Set(filtered.map(row => row.itemname))].sort();
    
    // Build table data structure - each item has sessions, each session has day data
    const items = itemnames.map(itemname => {
      const itemRows = filtered.filter(row => row.itemname === itemname);
      const cacheKey = `${selectedFC}_${selectedRestaurant}_${itemname}`;
      const sessions = sessionData[cacheKey] || [];
      
      // Create "All Sessions" row from day-level data
      const allSessionsRow = {
        session_type: 'All Sessions',
        session_name: 'All Sessions',
        dayData: dates.map(date => {
          const dayRow = itemRows.find(row => row.date === date);
          const { lower, upper } = getForecastBandFields(dayRow || {});
          return {
            date,
            actual: parseFloat(dayRow?.actual_qty || 0),
            existingModel: parseFloat(dayRow?.forecast_qty_old || 0),
            fridComponent: parseFloat(dayRow?.forecast_qty_component_model || 0),
            fridEnhanced: parseFloat(dayRow?.forecast_qty_frid || 0),
            friWeek: parseFloat(dayRow?.forecast_qty_friw || 0),
            forecastLower: lower,
            forecastUpper: upper,
            forecastRange: formatForecastRange(lower, upper),
            inRange: getInRangeLabel(dayRow || {}, dayRow?.actual_qty),
            dayRow,
          };
        })
      };
      
      // Only sessions where this item sold; chronological by session start (not A–Z)
      const sessionTypes = getSoldSessionTypesByTime(sessions);

      // Create session rows with day data
      const sessionRows = sessionTypes.map(sessionType => {
        // Get sessions of this type
        const typeSessions = sessions.filter(s => s.session_type === sessionType);
        
        // Group by date and aggregate
        const dayDataMap = {};
        dates.forEach(date => {
          dayDataMap[date] = {
            actual: 0,
            existingModel: 0,
            fridComponent: 0,
            fridEnhanced: 0,
            friWeek: 0
          };
        });
        
        typeSessions.forEach(session => {
          const date = session.date;
          if (dayDataMap[date]) {
            dayDataMap[date].actual += parseFloat(session.actual_qty || 0);
            // Prefer component model; fall back to session_forecast / forecast_qty
            const componentFc = parseFloat(
              session.forecast_qty_component_model
              ?? session.session_forecast
              ?? session.forecast_qty
              ?? 0
            );
            dayDataMap[date].existingModel += parseFloat(session.forecast_qty_old || 0);
            dayDataMap[date].fridComponent += Number.isFinite(componentFc) ? componentFc : 0;
            dayDataMap[date].fridEnhanced += parseFloat(session.forecast_qty_frid || 0);
            dayDataMap[date].friWeek += parseFloat(session.forecast_qty_friw || 0);
            const { lower, upper } = getSessionBandFields(session);
            if (Number.isFinite(lower) && Number.isFinite(upper)) {
              dayDataMap[date].forecastLower = (dayDataMap[date].forecastLower || 0) + lower;
              dayDataMap[date].forecastUpper = (dayDataMap[date].forecastUpper || 0) + upper;
            }
          }
        });
        
        // Get first session for this type to get session times
        const firstSession = typeSessions[0];
        
        return {
          session_type: sessionType,
          session_name: formatSessionName(
            sessionType,
            firstSession?.session_start_time,
            firstSession?.session_end_time
          ),
          dayData: dates.map(date => {
            const d = dayDataMap[date] || {
              actual: 0,
              existingModel: 0,
              fridComponent: 0,
              fridEnhanced: 0,
              friWeek: 0,
            };
            const lower = d.forecastLower;
            const upper = d.forecastUpper;
            return {
              date,
              ...d,
              forecastRange: formatForecastRange(lower, upper),
              inRange: (() => {
                const actual = d.actual || 0;
                if (!(actual > 0)) return '—';
                if (!Number.isFinite(lower) || !Number.isFinite(upper)) return '—';
                return actual >= lower && actual <= upper ? 'Yes' : 'No';
              })(),
            };
          })
        };
      });
      
      // Combine "All Sessions" with individual sessions
      const allSessionRows = [allSessionsRow, ...sessionRows];
      
      return {
        itemname,
        sessionRows: allSessionRows
      };
    });

    // Inject synthetic Non Veg Combo 1 + Combo 2 when both source items exist
    const combo1 = items.find((it) => normalizeItemKey(it.itemname) === 'non veg combo 1');
    const combo2 = items.find((it) => normalizeItemKey(it.itemname) === 'non veg combo 2');
    if (combo1 && combo2) {
      const all1 = combo1.sessionRows.find((s) => s.session_type === 'All Sessions');
      const all2 = combo2.sessionRows.find((s) => s.session_type === 'All Sessions');
      if (all1 && all2) {
        const dayData = dates.map((date) => {
          const d1 = all1.dayData.find((d) => d.date === date) || {};
          const d2 = all2.dayData.find((d) => d.date === date) || {};
          const sum = (a, b) => (parseFloat(a) || 0) + (parseFloat(b) || 0);
          const actual = sum(d1.actual, d2.actual);
          const lower = sum(d1.forecastLower, d2.forecastLower);
          const upper = sum(d1.forecastUpper, d2.forecastUpper);
          const hasLower = Number.isFinite(parseFloat(d1.forecastLower)) || Number.isFinite(parseFloat(d2.forecastLower));
          const hasUpper = Number.isFinite(parseFloat(d1.forecastUpper)) || Number.isFinite(parseFloat(d2.forecastUpper));
          return {
            date,
            actual,
            existingModel: sum(d1.existingModel, d2.existingModel),
            fridComponent: sum(d1.fridComponent, d2.fridComponent),
            fridEnhanced: sum(d1.fridEnhanced, d2.fridEnhanced),
            friWeek: sum(d1.friWeek, d2.friWeek),
            forecastLower: hasLower ? lower : null,
            forecastUpper: hasUpper ? upper : null,
            forecastRange: formatForecastRange(hasLower ? lower : null, hasUpper ? upper : null),
            inRange: (() => {
              if (!(actual > 0) || !hasLower || !hasUpper) return '—';
              return actual >= lower && actual <= upper ? 'Yes' : 'No';
            })(),
            dayRow: {
              foodcourtid: selectedFC,
              restaurant: selectedRestaurant,
              itemname: COMBO_AGGREGATE_ITEMNAME,
              date,
              is_combo_aggregate: true,
              description: [d1.dayRow?.description, d2.dayRow?.description].filter(Boolean).join(' | '),
            },
          };
        });
        items.push({
          itemname: COMBO_AGGREGATE_ITEMNAME,
          is_combo_aggregate: true,
          sessionRows: [{
            session_type: 'All Sessions',
            session_name: 'All Sessions',
            dayData,
          }],
        });
        items.sort((a, b) => compareItemDisplayOrder(a.itemname, b.itemname));
      }
    }
    
    return { items, days: dates };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const formatSessionName = (sessionType, startTime, endTime) => {
    const properSessionType = properCase(sessionType || '');
    if (startTime && endTime) {
      return `${properSessionType} (${startTime} - ${endTime})`;
    }
    return properSessionType;
  };

  const { items: allItems, days } = processTableData();
  
  // Filter items based on search query and combos filter
  const filteredItems = allItems.filter(item => {
    // Apply combos filter
    if (combosOnly) {
      if (!isComboItem(item.itemname)) return false;
    }
    // Apply search query filter
    if (!searchQuery.trim()) return true;
    const searchLower = searchQuery.toLowerCase();
    return item.itemname.toLowerCase().includes(searchLower);
  });
  
  // Pagination logic
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const items = filteredItems.slice(startIndex, endIndex);

  // Download filtered data as CSV
  const handleDownload = useCallback(() => {
    if (filteredItems.length === 0) {
      alert('No data to download');
      return;
    }

    // Helper functions for date formatting
    const formatDateForCSV = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatDayOfWeekForCSV = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    };

    // Prepare CSV headers (when showOnlyForecasts: no Accuracy/Status, no Actual; only forecast columns)
    const headers = [
      'Item Name',
      'Session',
      ...(!showOnlyForecasts ? ['Accuracy%', 'Abs. Error', 'Status'] : []),
      ...days.map(day => {
        const dayStr = formatDayOfWeekForCSV(day) + ' ' + formatDateForCSV(day);
        if (showOnlyForecasts) {
          if (isNewModelFormat) return [`${dayStr} - ${debugMode ? 'New' : 'Forecast'}`];
          return [`${dayStr} - FRID(C)`, `${dayStr} - FRID(E)`, `${dayStr} - FRIW`];
        }
        if (isNewModelFormat) {
          return [`${dayStr} - Actual`, `${dayStr} - ${debugMode ? 'New' : 'Forecast'}`];
        }
        return [`${dayStr} - Actual`, `${dayStr} - FRID(C)`, `${dayStr} - FRID(E)`, `${dayStr} - FRIW`];
      }).flat()
    ];

    // Prepare CSV rows - flatten session data
    const rows = [];
    filteredItems.forEach(item => {
      const itemMetrics = getItemMetrics(item.itemname);
      item.sessionRows.forEach((sessionRow, sessionIdx) => {
        const row = [
          item.itemname || '',
          sessionRow.session_name || 'All Sessions',
          ...(!showOnlyForecasts ? [
            sessionIdx === 0 ? (itemMetrics.mape !== null ? `${((1 - itemMetrics.mape) * 100).toFixed(1)}%` : 'N/A') : '',
            sessionIdx === 0 ? (itemMetrics.mad !== null ? itemMetrics.mad.toFixed(1) : 'N/A') : '',
            sessionIdx === 0 ? (itemMetrics.status || 'N/A') : ''
          ] : []),
          ...days.map(day => {
            const dayData = sessionRow.dayData.find(d => d.date === day);
            if (!dayData) {
              if (showOnlyForecasts) return isNewModelFormat ? ['0'] : ['0', '0', '0'];
              if (isNewModelFormat) return ['0', '0'];
              return ['0', '0', '0', '0'];
            }
            if (showOnlyForecasts) {
              if (isNewModelFormat) return [dayData.fridComponent?.toFixed(0) || '0'];
              return [
                dayData.fridComponent?.toFixed(0) || '0',
                dayData.fridEnhanced?.toFixed(0) || '0',
                dayData.friWeek?.toFixed(0) || '0'
              ];
            }
            if (isNewModelFormat) {
              return [
                dayData.actual?.toFixed(0) || '0',
                dayData.fridComponent?.toFixed(0) || '0'
              ];
            }
            return [
              dayData.actual?.toFixed(0) || '0',
              dayData.fridComponent?.toFixed(0) || '0',
              dayData.fridEnhanced?.toFixed(0) || '0',
              dayData.friWeek?.toFixed(0) || '0'
            ];
          }).flat()
        ];
        rows.push(row);
      });
    });

    // Combine headers and rows
    const csvContent = [
      headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `restaurant-level-forecast-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredItems, days, isNewModelFormat, debugMode, showOnlyForecasts, getItemMetrics]);
  
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading data: {error}</Alert>;
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                mb: 0.5
              }}
            >
              🏪 Restaurant Level Forecasts
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                <strong>Model Publish Date:</strong> {folderInfo?.publishDate || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                <strong>Forecast Week:</strong> {folderInfo?.forecastWeek || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          {/* Status Legend */}
          <Box sx={{ display: 'inline-block' }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
              Status Legend
            </Typography>
            <Box
              component="table"
              sx={{
                borderCollapse: 'collapse',
                fontSize: '0.65rem',
                border: '1px solid',
                borderColor: 'grey.300',
                '& td, & th': {
                  border: '1px solid',
                  borderColor: 'grey.300',
                  padding: '4px 6px',
                  textAlign: 'center',
                  minWidth: '50px',
                  fontSize: '0.65rem',
                  fontFamily: 'inherit'
                },
                '& th': {
                  bgcolor: 'grey.200',
                  fontWeight: 600,
                  fontSize: '0.65rem'
                }
              }}
            >
              <thead>
                <tr>
                  <th></th>
                  <th>Abs. Error ≤5</th>
                  <th>Abs. Error 6-15</th>
                  <th>Abs. Error &gt;15</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th style={{ bgcolor: '#f5f5f5', fontWeight: 600, fontSize: '0.65rem' }}>Accuracy% &gt;90%</th>
                  <td style={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem' }}>Green</td>
                  <td style={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem' }}>Green</td>
                  <td style={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem' }}>Green</td>
                </tr>
                <tr>
                  <th style={{ bgcolor: '#f5f5f5', fontWeight: 600, fontSize: '0.65rem' }}>Accuracy% 70-90%</th>
                  <td style={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem' }}>Green</td>
                  <td style={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.65rem' }}>Orange</td>
                  <td style={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.65rem' }}>Orange</td>
                </tr>
                <tr>
                  <th style={{ bgcolor: '#f5f5f5', fontWeight: 600, fontSize: '0.65rem' }}>Accuracy% &lt;70%</th>
                  <td style={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem' }}>Green</td>
                  <td style={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.65rem' }}>Orange</td>
                  <td style={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.65rem' }}>Red</td>
                </tr>
              </tbody>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Filters Card */}
      <Card 
        elevation={1}
        sx={{ 
          mb: 3,
          bgcolor: 'primary.light',
          background: 'rgba(25, 118, 210, 0.08)'
        }}
      >
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                size="small"
                options={foodcourts}
                getOptionLabel={(option) => formatFcDisplayName(option?.id, fcMapping, foodcourts) || option?.name || ''}
                value={selectedFC ? {
                  id: selectedFC,
                  name: formatFcDisplayName(selectedFC, fcMapping, foodcourts),
                } : null}
                onChange={(event, newValue) => {
                  setSelectedFC(newValue ? newValue.id : '');
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Food Court"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '0.75rem' // 12px (reduced from 14px by 2px)
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ fontSize: '0.75rem' }}>
                    {option.name}
                  </Box>
                )}
                sx={{
                  '& .MuiAutocomplete-input': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                disabled={!selectedFC}
                options={restaurants}
                getOptionLabel={(option) => formatRestaurantDisplayName(option?.id, restMapping, restaurants) || option?.name || ''}
                value={selectedRestaurant ? {
                  id: selectedRestaurant,
                  name: formatRestaurantDisplayName(selectedRestaurant, restMapping, restaurants),
                  numFris: restaurants.find((rest) => rest.id === selectedRestaurant)?.numFris || 0
                } : null}
                onChange={(event, newValue) => {
                  setSelectedRestaurant(newValue ? newValue.id : '');
                }}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Restaurant"
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: '0.75rem' // 12px (reduced from 14px by 2px)
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} sx={{ fontSize: '0.75rem' }}>
                    {option.name}
                    {option.numFris > 0 && (
                      <Box
                        component="span"
                        sx={{
                          fontStyle: 'italic',
                          color: 'text.secondary',
                          ml: 0.5
                        }}
                      >
                        ({option.numFris})
                      </Box>
                    )}
                  </Box>
                )}
                sx={{
                  '& .MuiAutocomplete-input': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {selectedFC && selectedRestaurant && allItems.length > 0 && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search items by name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '0.75rem' // 12px (reduced from 14px by 2px)
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem'
                    }
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} md={2}>
              {selectedFC && selectedRestaurant && allItems.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={combosOnly}
                      onChange={(e) => {
                        setCombosOnly(e.target.checked);
                        setPage(1); // Reset to first page when filter changes
                      }}
                      size="small"
                    />
                  }
                  label="Combos Only"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.75rem' // 12px (reduced from 14px by 2px)
                    }
                  }}
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      {selectedFC && selectedRestaurant ? (
        items.length > 0 && days.length > 0 ? (
          <Card elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)', color: 'text.primary' }}>
                Restaurant Level Forecast
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<BarChartIcon />}
                  onClick={() => setDailySalesDialogOpen(true)}
                  size="small"
                  disabled={!selectedFC || !selectedRestaurant}
                >
                  Daily Sales Chart
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  size="small"
                  disabled={filteredItems.length === 0}
                >
                  Download All ({filteredItems.length})
                </Button>
              </Box>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                stickyHeader
                size="small"
                sx={{
                  tableLayout: 'auto',
                  whiteSpace: 'nowrap',
                  '& .MuiTableCell-root': { 
                    padding: '4px 8px',
                    fontSize: '0.7rem' // Reduced by 2px from default 0.8rem
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 170, minWidth: 170, position: 'sticky', left: 0, zIndex: 12, bgcolor: 'background.paper', verticalAlign: 'middle' }}>
                      Item Name
                    </TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 130, minWidth: 130, position: 'sticky', left: 170, zIndex: 11, bgcolor: 'background.paper', verticalAlign: 'middle' }}>
                      Session
                    </TableCell>
                    {!showOnlyForecasts && (
                      <>
                        <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 50, minWidth: 50, maxWidth: 60, verticalAlign: 'middle' }}>
                          Accuracy%
                        </TableCell>
                        <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 50, minWidth: 50, maxWidth: 60, verticalAlign: 'middle' }}>
                          Abs. Error
                        </TableCell>
                        {isNewModelFormat && (
                          <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 60, minWidth: 60, maxWidth: 70, verticalAlign: 'middle' }}>
                            In Range%
                          </TableCell>
                        )}
                        <TableCell rowSpan={2} align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', width: 110, minWidth: 110, verticalAlign: 'middle' }}>
                          Status
                        </TableCell>
                      </>
                    )}
                    {days.map((day, dayIdx) => (
                      <React.Fragment key={day}>
                        <TableCell 
                          colSpan={showOnlyForecasts ? (isNewModelFormat ? 1 : 3) : (isNewModelFormat ? 5 : 10)} 
                          align="center" 
                          sx={{ 
                            fontWeight: 600, 
                            bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200',
                            borderLeft: dayIdx > 0 ? '2px solid' : 'none',
                            borderColor: 'divider'
                          }}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                              {formatDayOfWeek(day)} {formatDate(day)}
                            </Typography>
                          </Box>
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    {days.map((day, dayIdx) => (
                      <React.Fragment key={day}>
                        {!showOnlyForecasts && (
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider', minWidth: 80 }}>
                            Act
                          </TableCell>
                        )}
                        {isNewModelFormat ? (
                          showOnlyForecasts ? (
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider', minWidth: 80 }}>
                              {debugMode ? 'New' : 'Forecast'}
                            </TableCell>
                          ) : (
                            <>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                {debugMode ? 'New' : 'Forecast'}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Acc%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Abs Err
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 55 }}>
                                In Range
                              </TableCell>
                            </>
                          )
                        ) : (
                          showOnlyForecasts ? (
                            <>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider', minWidth: 80 }}>
                                FRID(C)
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                FRID(E)
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                FRIW
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                FRID(C)
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Acc%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Abs Err
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                FRID(E)
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Acc%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Abs Err
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.7rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 80 }}>
                                FRIW
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Acc%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.6rem', color: 'text.primary', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 60 }}>
                                Abs Err
                              </TableCell>
                            </>
                          )
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => {
                    // Calculate visible sessions once per item
                    // By default, non-"All Sessions" are collapsed (not in collapsedSessions means collapsed)
                    const visibleSessions = item.sessionRows.filter((_, sessionIdx) => {
                      if (sessionIdx === 0) return true; // Always show "All Sessions"
                      const key = `${item.itemname}_${sessionIdx}`;
                      // If key exists and is false, it's expanded; if doesn't exist or is true, it's collapsed
                      return collapsedSessions[key] === false;
                    });
                    const rowSpan = visibleSessions.length;
                    
                    // Check if all non-"All Sessions" are collapsed
                    const nonAllSessions = item.sessionRows.slice(1);
                    const allCollapsed = nonAllSessions.every((_, idx) => {
                      const key = `${item.itemname}_${idx + 1}`;
                      return collapsedSessions[key] !== false; // Not explicitly expanded means collapsed
                    });
                    
                    // Get item metrics for this item
                    const itemStrictRows = strictData.filter(
                      (row) =>
                        row.foodcourtid === selectedFC &&
                        row.restaurant === selectedRestaurant &&
                        row.itemname === item.itemname
                    );
                    const itemMetrics = getItemMetrics(item.itemname, itemStrictRows);
                    
                    const rowBg = idx % 2 === 0 ? 'background.paper' : 'grey.50';
                    return (
                    <React.Fragment key={idx}>
                        {item.sessionRows.map((sessionRow, sessionIdx) => {
                          const sessionKey = `${item.itemname}_${sessionIdx}`;
                          const isCollapsed = collapsedSessions[sessionKey] !== false; // Default to collapsed if not explicitly expanded
                          const isAllSessions = sessionRow.session_type === 'All Sessions';
                          
                          // Skip rendering if this session is collapsed
                          if (!isAllSessions && isCollapsed) {
                            return null;
                          }
                          
                          return (
                        <TableRow key={`${idx}-${sessionIdx}`} hover sx={{ bgcolor: rowBg }}>
                          {sessionIdx === 0 && (
                            <>
                              <TableCell 
                                    rowSpan={rowSpan}
                                sx={{ 
                                  position: 'sticky', 
                                  left: 0, 
                                  zIndex: 9, 
                                  bgcolor: 'background.paper',
                                  verticalAlign: 'top',
                                  width: 200,
                                  minWidth: 200
                                }}
                              >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <IconButton 
                                    size="small"
                                        onClick={(e) => toggleAllSessionsForItem(e, item.itemname, item.sessionRows)}
                                        sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }, padding: '2px' }}
                                  >
                                        {allCollapsed ? <ExpandMoreIcon sx={{ fontSize: '0.7rem' }} /> : <ExpandLessIcon sx={{ fontSize: '0.7rem' }} />}
                                  </IconButton>
                                  <Typography 
                                    component="span"
                                    variant="body2" 
                                    onClick={(e) => handleItemNameClick(e, item)}
                                    sx={{ 
                                      fontWeight: 500,
                                          fontSize: '0.7rem',
                                      color: 'primary.main',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      flex: 1,
                                      '&:hover': { color: 'primary.dark' }
                                    }}
                                  >
                                    {displayItemLabel(item.itemname)}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </>
                          )}
                              <TableCell 
                                sx={{ 
                                  position: 'sticky', 
                              left: 170, 
                              zIndex: sessionIdx === 0 ? 9 : 8, 
                                  bgcolor: 'background.paper',
                                  fontWeight: isAllSessions ? 400 : 400, // Remove bold from "All Sessions"
                                  fontSize: '0.7rem',
                                  width: 150,
                                  minWidth: 150,
                                  verticalAlign: sessionIdx === 0 ? 'top' : 'middle'
                            }}
                          >
                            {properCase(sessionRow.session_name)}
                          </TableCell>
                          {sessionIdx === 0 && !showOnlyForecasts && (
                            <>
                              <TableCell 
                                rowSpan={rowSpan}
                                align="center"
                                sx={{ 
                                  verticalAlign: 'top',
                                  fontSize: '0.75rem',
                                  width: 60,
                                  minWidth: 60,
                                  maxWidth: 60
                                }}
                              >
                                {itemMetrics.mape !== null ? `${((1 - itemMetrics.mape) * 100).toFixed(1)}%` : 'N/A'}
                              </TableCell>
                              <TableCell 
                                rowSpan={rowSpan}
                                align="center"
                                sx={{ 
                                  verticalAlign: 'top',
                                  fontSize: '0.75rem',
                                  width: 60,
                                  minWidth: 60,
                                  maxWidth: 60
                                }}
                              >
                                {itemMetrics.mad !== null ? itemMetrics.mad.toFixed(1) : 'N/A'}
                              </TableCell>
                              {isNewModelFormat && (
                                <TableCell
                                  rowSpan={rowSpan}
                                  align="center"
                                  sx={{
                                    verticalAlign: 'top',
                                    fontSize: '0.75rem',
                                    width: 60,
                                    minWidth: 60,
                                    maxWidth: 70
                                  }}
                                >
                                  {itemMetrics.inRangePct != null && Number.isFinite(itemMetrics.inRangePct)
                                    ? `${(itemMetrics.inRangePct * 100).toFixed(0)}%`
                                    : 'N/A'}
                                </TableCell>
                              )}
                              <TableCell 
                                rowSpan={rowSpan}
                                align="center"
                                sx={{ 
                                  verticalAlign: 'top',
                                  fontSize: '0.75rem',
                                  width: 120,
                                  minWidth: 120
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={itemMetrics.status || 'N/A'}
                                    color={getStatusColor(itemMetrics.status)}
                                    size="small"
                                    sx={{ 
                                      fontWeight: 600,
                                      fontSize: '0.6rem',
                                      height: '20px'
                                    }}
                                  />
                                  <Tooltip 
                                    title={
                                      <ReasonTooltipContent 
                                        status={itemMetrics.status}
                                        preReasons={itemMetrics.preReasonEntries}
                                        postReasons={itemMetrics.postReasonEntries}
                                        fallbackReason={itemMetrics.badForecastReason}
                                      />
                                    } 
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: 'rgba(0, 0, 0, 0.9)',
                                          fontSize: '0.85rem',
                                          maxWidth: 400,
                                          whiteSpace: 'pre-line'
                                        }
                                      }
                                    }}
                                  >
                                    <IconButton 
                                      size="small" 
                                      sx={{ 
                                        padding: '2px',
                                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                    >
                                      <InfoIcon 
                                        sx={{ 
                                          fontSize: '12px', 
                                          color: 'grey.400' 
                                        }} 
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </>
                          )}
                          {sessionRow.dayData.map((dayData, dayIdx) => {
                            const actual = dayData.actual || 0;
                            const componentModelMape = calculateDayMAPE(actual, dayData.fridComponent);
                            const componentModelMad = calculateDayMAD(actual, dayData.fridComponent);
                            const enhancedModelMape = calculateDayMAPE(actual, dayData.fridEnhanced);
                            const enhancedModelMad = calculateDayMAD(actual, dayData.fridEnhanced);
                            const weekModelMape = calculateDayMAPE(actual, dayData.friWeek);
                            const weekModelMad = calculateDayMAD(actual, dayData.friWeek);
                            const cellSx = { color: 'text.primary', fontSize: '0.7rem', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 90 };
                            const cellSxLeft = { ...cellSx, borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider' };
                            const accSx = { color: 'text.primary', fontSize: '0.65rem', bgcolor: dayIdx % 2 === 0 ? 'grey.100' : 'grey.200', minWidth: 70 };
                            return (
                            <React.Fragment key={dayIdx}>
                              {!showOnlyForecasts && (
                                <TableCell align="center" sx={{ ...cellSxLeft, fontWeight: 600 }}>
                                  {Number.isFinite(actual) ? actual.toFixed(0) : '0'}
                                </TableCell>
                              )}
                              {isNewModelFormat ? (
                                showOnlyForecasts ? (
                                  <TableCell align="center" sx={{ ...cellSxLeft, borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider' }}>
                                    <Box sx={{ lineHeight: 1.2 }}>
                                      <Typography component="span" sx={{ fontSize: '0.7rem' }}>
                                        {dayData.fridComponent?.toFixed(0) || '0'}
                                      </Typography>
                                      {dayData.forecastRange && (
                                        <Typography component="div" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                                          {dayData.forecastRange}
                                        </Typography>
                                      )}
                                    </Box>
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell align="center" sx={cellSx}>
                                      <Box sx={{ lineHeight: 1.2 }}>
                                        <Typography component="span" sx={{ fontSize: '0.7rem' }}>
                                          {dayData.fridComponent?.toFixed(0) || '0'}
                                        </Typography>
                                        {dayData.forecastRange && (
                                          <Typography component="div" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                                            {dayData.forecastRange}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {componentModelMape !== null ? `${((1 - componentModelMape) * 100).toFixed(1)}%` : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {componentModelMad !== null ? componentModelMad.toFixed(1) : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {dayData.inRange && dayData.inRange !== '—' ? (
                                        <Chip
                                          label={dayData.inRange}
                                          size="small"
                                          color={getInRangeChipColor(dayData.inRange)}
                                          sx={{ fontSize: '0.55rem', height: 18, minWidth: 36 }}
                                        />
                                      ) : ''}
                                    </TableCell>
                                  </>
                                )
                              ) : (
                                showOnlyForecasts ? (
                                  <>
                                    <TableCell align="center" sx={{ ...cellSxLeft, borderLeft: dayIdx > 0 ? '2px solid' : 'none', borderColor: 'divider' }}>
                                      {dayData.fridComponent?.toFixed(0) || '0'}
                                    </TableCell>
                                    <TableCell align="center" sx={cellSx}>
                                      {dayData.fridEnhanced?.toFixed(0) || '0'}
                                    </TableCell>
                                    <TableCell align="center" sx={cellSx}>
                                      {dayData.friWeek?.toFixed(0) || '0'}
                                    </TableCell>
                                  </>
                                ) : (
                                  <>
                                    <TableCell align="center" sx={cellSx}>
                                      {dayData.fridComponent?.toFixed(0) || '0'}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {componentModelMape !== null ? `${((1 - componentModelMape) * 100).toFixed(1)}%` : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {componentModelMad !== null ? componentModelMad.toFixed(1) : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={cellSx}>
                                      {dayData.fridEnhanced?.toFixed(0) || '0'}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {enhancedModelMape !== null ? `${((1 - enhancedModelMape) * 100).toFixed(1)}%` : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {enhancedModelMad !== null ? enhancedModelMad.toFixed(1) : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={cellSx}>
                                      {dayData.friWeek?.toFixed(0) || '0'}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {weekModelMape !== null ? `${((1 - weekModelMape) * 100).toFixed(1)}%` : ''}
                                    </TableCell>
                                    <TableCell align="center" sx={accSx}>
                                      {weekModelMad !== null ? weekModelMad.toFixed(1) : ''}
                                    </TableCell>
                                  </>
                                )
                              )}
                            </React.Fragment>
                            );
                          })}
                        </TableRow>
                          );
                        })}
                                                </React.Fragment>
                                              );
                                            })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        ) : (
          <Alert severity="info">No Data Found or Models Trained on this Day for the Restaurant. Please Try for Another date</Alert>
        )
      ) : (
        <Alert severity="info">Please select a Food Court and Restaurant to view data.</Alert>
      )}
      
      {/* Pagination */}
      {selectedFC && selectedRestaurant && filteredItems.length > itemsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Daily Sales Chart Dialog */}
      <Dialog
        open={dailySalesDialogOpen}
        onClose={() => setDailySalesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', minHeight: '44px', py: 1.5, px: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
            Restaurant Daily Sales Chart
            {selectedFC && selectedRestaurant && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {hardcodedFcMapping[selectedFC] || fcMapping[selectedFC] || selectedFC} - {restMapping[selectedRestaurant] || selectedRestaurant}
              </Typography>
            )}
          </Typography>
          <IconButton onClick={() => setDailySalesDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.08)', borderBottom: '1px solid', borderColor: 'divider', borderRadius: 1, minHeight: '44px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 'calc(1.25rem - 2px)' }}>
                Daily Sales - Last {(() => {
                  const timePeriodConfig = {
                    '1month': '1 Month',
                    '3months': '3 Months',
                    '6months': '6 Months',
                    '1year': '1 Year',
                    '2years': '2 Years'
                  };
                  return timePeriodConfig[timePeriod] || '6 Months';
                })()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Time Period</InputLabel>
                  <Select
                    value={timePeriod}
                    label="Time Period"
                    onChange={(e) => setTimePeriod(e.target.value)}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="1month">1 Month</MenuItem>
                    <MenuItem value="3months">3 Months</MenuItem>
                    <MenuItem value="6months">6 Months</MenuItem>
                    <MenuItem value="1year">1 Year</MenuItem>
                    <MenuItem value="2years">2 Years</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Days of Week</InputLabel>
                  <Select
                    multiple
                    value={selectedDays}
                    onChange={handleDayFilterChange}
                    label="Days of Week"
                    renderValue={(selected) =>
                      selected.length === DAY_NAME_OPTIONS.length ? 'All Days' : `${selected.length} Selected`
                    }
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem key="select-all" value={ALL_DAYS_VALUE}>
                      <Checkbox checked={selectedDays.length === DAY_NAME_OPTIONS.length} />
                      <ListItemText primary="Select All" />
                    </MenuItem>
                    {DAY_NAME_OPTIONS.map((day) => (
                      <MenuItem key={day} value={day}>
                        <Checkbox checked={selectedDays.indexOf(day) > -1} />
                        <ListItemText primary={day} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Date Range Slider - Compressed */}
                {dailySalesData.length > 0 && dateRange[0] && dateRange[1] && !Number.isNaN(dateRange[0].getTime()) && !Number.isNaN(dateRange[1].getTime()) && (() => {
                  // Get model date for calculating min date based on time period
                  let modelDateObj = null;
                  if (folderInfo?.modelDate) {
                    try {
                      const year = folderInfo.modelDate.substring(0, 4);
                      const month = folderInfo.modelDate.substring(4, 6);
                      const day = folderInfo.modelDate.substring(6, 8);
                      modelDateObj = new Date(`${year}-${month}-${day}`);
                    } catch (e) {
                      console.error('Error parsing model date:', e);
                    }
                  }
                  if (!modelDateObj && selectedFolder) {
                    try {
                      const parts = selectedFolder.split('_');
                      if (parts.length >= 1) {
                        const modelDateStr = parts[0];
                        const year = modelDateStr.substring(0, 4);
                        const month = modelDateStr.substring(4, 6);
                        const day = modelDateStr.substring(6, 8);
                        modelDateObj = new Date(`${year}-${month}-${day}`);
                      }
                    } catch (e) {
                      console.error('Error parsing model date:', e);
                    }
                  }
                  
                  if (!modelDateObj) return null;
                  
                  // Get time period configuration
                  const timePeriodConfig = {
                    '1month': { days: 28 },
                    '3months': { days: 91 },
                    '6months': { days: 182 },
                    '1year': { days: 364 },
                    '2years': { days: 730 }
                  };
                  const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
                  
                  // Calculate min date based on time period (model_date - period days)
                  const minDate = new Date(modelDateObj);
                  minDate.setDate(minDate.getDate() - selectedConfig.days);
                  
                  // Max date is model_date + 7 days (for forecast)
                  const maxDate = new Date(modelDateObj);
                  maxDate.setDate(maxDate.getDate() + 7);
                  
                  const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24));
                  
                  // Convert date range to slider values (0-100)
                  const startPercent = Math.max(0, Math.min(100, ((dateRange[0] - minDate) / (maxDate - minDate)) * 100));
                  const endPercent = Math.max(0, Math.min(100, ((dateRange[1] - minDate) / (maxDate - minDate)) * 100));
                  
                  const handleSliderChange = (event, newValue) => {
                    const [startPercent, endPercent] = newValue;
                    const startDate = new Date(minDate);
                    startDate.setDate(startDate.getDate() + Math.round((startPercent / 100) * totalDays));
                    const endDate = new Date(minDate);
                    endDate.setDate(endDate.getDate() + Math.round((endPercent / 100) * totalDays));
                    setDateRange([startDate, endDate]);
                  };
                  
                  return (
                    <FormControl size="small" sx={{ minWidth: 200, position: 'relative' }}>
                      <InputLabel 
                        shrink={true}
                        sx={{ 
                          bgcolor: 'rgba(76, 175, 80, 0.08)',
                          px: 0.5,
                          transform: 'translate(14px, -9px) scale(0.75)',
                          transformOrigin: 'top left',
                          zIndex: 1
                        }}
                      >
                        Date Range Filter
                      </InputLabel>
                      <Box sx={{ 
                        width: 200, 
                        height: '40px',
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        bgcolor: 'transparent',
                        borderRadius: 1,
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        justifyContent: 'center',
                        px: 1,
                        position: 'relative',
                        '&:hover': {
                          borderColor: 'rgba(0, 0, 0, 0.87)'
                        },
                        '&:focus-within': {
                          borderColor: 'primary.main',
                          borderWidth: '2px'
                        }
                      }}>
                        <Slider
                          value={[startPercent, endPercent]}
                          onChange={handleSliderChange}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => {
                            const date = new Date(minDate);
                            date.setDate(date.getDate() + Math.round((value / 100) * totalDays));
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          }}
                          min={0}
                          max={100}
                          size="small"
                          sx={{ width: '90%' }}
                        />
                      </Box>
                    </FormControl>
                  );
                })()}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={combosOnlyChart}
                      onChange={(e) => setCombosOnlyChart(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Combos Only"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showNonZeroOnly}
                      onChange={(e) => setShowNonZeroOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Non-Zero Days Only"
                />
              </Box>
            </Box>
            {loadingSales ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            ) : salesError ? (
              <Alert severity="error">Error loading sales data: {salesError}</Alert>
            ) : dailySalesData.length === 0 ? (
              <Alert severity="info">No sales data available for this restaurant</Alert>
            ) : (() => {
              // Get model date from folderInfo or selectedFolder
              let modelDateObj = null;
              if (folderInfo?.modelDate) {
                try {
                  const year = folderInfo.modelDate.substring(0, 4);
                  const month = folderInfo.modelDate.substring(4, 6);
                  const day = folderInfo.modelDate.substring(6, 8);
                  modelDateObj = new Date(`${year}-${month}-${day}`);
                } catch (e) {
                  console.error('Error parsing model date from folderInfo:', e);
                }
              }
              // Fallback: parse from selectedFolder (format: YYYYMMDD_YYYYMMDD)
              if (!modelDateObj && selectedFolder) {
                try {
                  const parts = selectedFolder.split('_');
                  if (parts.length >= 1) {
                    const modelDateStr = parts[0]; // First part is model date
                    const year = modelDateStr.substring(0, 4);
                    const month = modelDateStr.substring(4, 6);
                    const day = modelDateStr.substring(6, 8);
                    modelDateObj = new Date(`${year}-${month}-${day}`);
                  }
                } catch (e) {
                  console.error('Error parsing model date from selectedFolder:', e);
                }
              }
              
              const timePeriodConfig = {
                '1month': { days: 28, label: '1 Month' },
                '3months': { days: 91, label: '3 Months' },
                '6months': { days: 182, label: '6 Months' },
                '1year': { days: 364, label: '1 Year' },
                '2years': { days: 730, label: '2 Years' }
              };
              const selectedConfig = timePeriodConfig[timePeriod] || timePeriodConfig['6months'];
              
              // Filter data based on selected time period relative to model date
              let periodData = dailySalesData;
              if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
                const periodStart = new Date(modelDateObj);
                periodStart.setDate(periodStart.getDate() - selectedConfig.days);
                const periodEnd = new Date(modelDateObj);
                periodEnd.setDate(periodEnd.getDate() + 7);
                periodData = dailySalesData.filter(item => {
                  const itemDate = new Date(item.date);
                  if (Number.isNaN(itemDate.getTime())) return false;
                  return itemDate >= periodStart && itemDate <= periodEnd;
                });
              } else {
                periodData = dailySalesData.slice(-selectedConfig.days);
              }
              
              // Apply date range slider filter if set
              let dateFilteredData = periodData;
              if (dateRange[0] && dateRange[1] && !Number.isNaN(dateRange[0].getTime()) && !Number.isNaN(dateRange[1].getTime())) {
                dateFilteredData = periodData.filter(item => {
                  const itemDate = new Date(item.date);
                  if (Number.isNaN(itemDate.getTime())) return false;
                  return itemDate >= dateRange[0] && itemDate <= dateRange[1];
                });
              }
              
              // Filter data based on toggle state
              let filteredData = dateFilteredData;
              if (showNonZeroOnly) {
                filteredData = dateFilteredData.filter(item => {
                  const hasSales = parseFloat(item.quantity || 0) > 0;
                  const fc = item.forecast_component ?? item.forecast_qty_component_model ?? item.forecast_qty;
                  const hasForecast = parseFloat(fc || 0) > 0;
                  return hasSales || hasForecast;
                });
              }

              const activeDays = selectedDays.length > 0 ? selectedDays : DAY_NAME_OPTIONS.slice(1, 6);
              const activeDaySet = new Set(activeDays);
              const deriveDayName = (value) => {
                const date = new Date(value);
                return date.toLocaleDateString('en-US', { weekday: 'long' });
              };

              filteredData = filteredData.filter(item => {
                const dayName = deriveDayName(item.date);
                return activeDaySet.has(dayName);
              });
              
              // Calculate mean
              const soldDays = periodData.filter(item => parseFloat(item.quantity || 0) > 0);
              const totalQuantity = soldDays.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0);
              const meanQuantity = soldDays.length > 0 ? totalQuantity / soldDays.length : 0;
              
              // Calculate Model Date + 1 (forecast starts the day after model date)
              let modelDatePlusOne = null;
              if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
                modelDatePlusOne = new Date(modelDateObj);
                modelDatePlusOne.setDate(modelDatePlusOne.getDate() + 1);
                modelDatePlusOne.setHours(0, 0, 0, 0); // Normalize to start of day
              }
              
              // Prepare chart data
              const chartData = filteredData.map((item) => {
                const itemDate = new Date(item.date);
                itemDate.setHours(0, 0, 0, 0); // Normalize to start of day
                const quantity = parseFloat(item.quantity || 0);
                let isPostModelDate = false;
                if (modelDateObj && !Number.isNaN(modelDateObj.getTime()) && !Number.isNaN(itemDate.getTime())) {
                  isPostModelDate = itemDate > modelDateObj;
                }
                
                // Within forecast window: use plan value, or 0 when no prediction (weekends etc.)
                const fcRaw = item.forecast_component ?? item.forecast_qty_component_model ?? item.forecast_qty;
                let forecastValue = null;
                if (modelDatePlusOne && !Number.isNaN(modelDatePlusOne.getTime()) && !Number.isNaN(itemDate.getTime())) {
                  if (itemDate >= modelDatePlusOne) {
                    const forecast = parseFloat(fcRaw);
                    forecastValue = Number.isFinite(forecast) && forecast > 0 ? forecast : 0;
                  }
                } else {
                  const forecast = parseFloat(fcRaw);
                  forecastValue = Number.isFinite(forecast) && forecast > 0 ? forecast : 0;
                }
                
                // Create chart data object, excluding forecast_pre to avoid showing it in legend
                const chartItem = {
                  date: item.date,
                  quantity: quantity,
                  quantity_post: isPostModelDate ? quantity : null,
                  quantity_post_area: isPostModelDate ? quantity : null,
                  mean: parseFloat(meanQuantity.toFixed(1)),
                  forecast_plot: forecastValue
                };
                return chartItem;
              });
              
              const forecastDataPoints = chartData.filter(
                (item) => item.forecast_plot !== null && item.forecast_plot !== undefined
              );
              // Only highlight the last forecast point with a solid dot
              const lastForecastDate =
                forecastDataPoints.length > 0
                  ? forecastDataPoints[forecastDataPoints.length - 1].date
                  : null;
              const showForecastDots = !!lastForecastDate;
              
              const formatDate = (value) => {
                const date = new Date(value);
                if (Number.isNaN(date.getTime())) return value;
                const day = date.getDate().toString().padStart(2, '0');
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const month = monthNames[date.getMonth()];
                const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
                return `${day}-${month} (${dayOfWeek})`;
              };
              
              let modelDateForLine = null;
              if (modelDateObj && !Number.isNaN(modelDateObj.getTime())) {
                modelDateForLine = modelDateObj.toISOString().split('T')[0];
              }
              
              // When "Non-Zero Days Only" is enabled, continue to show a line chart,
              // but using only the filtered non-zero data points and connecting gaps.
              if (showNonZeroOnly) {
                return (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tickFormatter={formatDate}
                      />
                      <YAxis 
                        label={{ value: 'Sales Quantity', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      {modelDateForLine && (
                        <ReferenceLine 
                          x={modelDateForLine} 
                          stroke="#666" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: "Model Date", position: "top", fill: "#666", fontSize: 12 }}
                        />
                      )}
                      {!showOnlyForecasts && (
                        <>
                          <Line 
                            type="monotone" 
                            dataKey="quantity" 
                            stroke="#1976d2" 
                            strokeWidth={2}
                            dot={false}
                            connectNulls={true}
                            name="Sales Quantity"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="quantity_post" 
                            stroke="#1976d2" 
                            strokeWidth={1.5}
                            dot={false}
                            connectNulls={true}
                            name=""
                            legendType="none"
                          />
                          <Area
                            type="monotone"
                            dataKey="quantity_post_area"
                            stroke="none"
                            fill="#1976d2"
                            fillOpacity={0.15}
                            isAnimationActive={false}
                            name=""
                            legendType="none"
                          />
                        </>
                      )}
                      <Line 
                        type="monotone" 
                        dataKey="mean" 
                        stroke="#4caf50" 
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                        name={`Mean (${meanQuantity.toFixed(1)})`}
                      />
                      <Line
                        type="monotone"
                        dataKey="forecast_plot"
                        stroke="#ef5350"
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        dot={
                          showForecastDots
                            ? (props) =>
                                props?.payload?.date === lastForecastDate ? (
                                  <circle
                                    cx={props.cx}
                                    cy={props.cy}
                                    r={4}
                                    fill="#ef5350"
                                    stroke="#ef5350"
                                  />
                                ) : null
                            : false
                        }
                        connectNulls={true}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        name="Forecast"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                );
              } else {
                return (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tickFormatter={formatDate}
                      />
                      <YAxis 
                        label={{ value: 'Sales Quantity', angle: -90, position: 'insideLeft' }}
                        tick={{ fontSize: 12 }}
                      />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      {modelDateForLine && (
                        <ReferenceLine 
                          x={modelDateForLine} 
                          stroke="#666" 
                          strokeDasharray="5 5" 
                          strokeWidth={2}
                          label={{ value: "Model Date", position: "top", fill: "#666", fontSize: 12 }}
                        />
                      )}
                      {!showOnlyForecasts && (
                        <>
                          <Line 
                            type="monotone" 
                            dataKey="quantity" 
                            stroke="#1976d2" 
                            strokeWidth={2}
                            dot={false}
                            connectNulls={true}
                            name="Sales Quantity"
                          />
                          <Line 
                            type="monotone" 
                            dataKey="quantity_post" 
                            stroke="#1976d2" 
                            strokeWidth={1.5}
                            dot={false}
                            connectNulls={true}
                            name=""
                            legendType="none"
                          />
                          <Area
                            type="monotone"
                            dataKey="quantity_post_area"
                            stroke="none"
                            fill="#1976d2"
                            fillOpacity={0.15}
                            isAnimationActive={false}
                            name=""
                            legendType="none"
                          />
                        </>
                      )}
                      <Line 
                        type="monotone" 
                        dataKey="mean" 
                        stroke="#4caf50" 
                        strokeWidth={1.5}
                        strokeDasharray="3 3"
                        dot={false}
                        name={`Mean (${meanQuantity.toFixed(1)})`}
                      />
                      <Line
                        type="monotone"
                        dataKey="forecast_plot"
                        stroke="#ef5350"
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        dot={
                          showForecastDots
                            ? (props) =>
                                props?.payload?.date === lastForecastDate ? (
                                  <circle
                                    cx={props.cx}
                                    cy={props.cy}
                                    r={4}
                                    fill="#ef5350"
                                    stroke="#ef5350"
                                  />
                                ) : null
                            : false
                        }
                        connectNulls={true}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        name="Forecast"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                );
              }
            })()}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default RestaurantLevel;

